import React, { useContext, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import AppModal from "../../../components/AppModal";
import AuthField from "../../../components/Auth/AuthField";
import QueueCheckboxList from "../../../components/QueueCheckboxList";
import WizardStepper, { WizardStepPanel } from "../../../components/WizardStepper";
import api from "../../../services/api";
import toastError from "../../../errors/toastError";
import { WhatsAppsContext } from "../../../context/WhatsApp/WhatsAppsContext";
import { i18n } from "../../../translate/i18n";
import { getConnectionWizardSteps } from "../connectionWizardSteps";
import { NATIVE_CHANNELS } from "../connectionChannelsCatalog";

const INITIAL_FORM = {
	name: "",
	greetingMessage: "",
	farewellMessage: "",
	isDefault: false,
	email: "",
	emailPassword: "",
	emailServer: "",
	emailPort: "",
	emailServerSend: "",
	emailPortSend: "",
	wabaFoneNumberId: "",
	wabaAccountId: "",
	wabaAccessToken: "",
	pageId: "",
	pageToken: "",
	telegramBotToken: "",
	maxAttempts: 3,
};

function ChannelBanner({ channelMeta }) {
	if (!channelMeta) return null;

	return (
		<div className="mb-5 flex items-center gap-3 rounded-xl border border-default-200 bg-default-50 px-4 py-3 dark:border-default-700 dark:bg-default-900/60">
			<div className="flex size-11 shrink-0 items-center justify-center rounded-lg bg-white dark:bg-default-800">
				<img src={channelMeta.icon} alt="" className="size-7 object-contain" />
			</div>
			<div className="min-w-0">
				<p className="mb-0 text-xs text-default-500 dark:text-default-400">
					{i18n.t("connections.wizard.channelSelected")}
				</p>
				<p className="mb-0 truncate text-sm font-semibold text-default-900 dark:text-default-50">
					{i18n.t(channelMeta.labelKey)}
				</p>
			</div>
		</div>
	);
}

function WizardFooter({ onClose, onBack, onNext, onSubmit, showBack, isLastStep, saving }) {
	return (
		<div className="flex w-full items-center gap-2">
			<button type="button" className="btn btn-sub-gray" onClick={onClose} disabled={saving}>
				{i18n.t("whatsappModal.buttons.cancel")}
			</button>
			{showBack && (
				<button type="button" className="btn btn-sub-gray" onClick={onBack} disabled={saving}>
					{i18n.t("connections.wizard.back")}
				</button>
			)}
			<div className="flex-1" />
			{isLastStep ? (
				<button type="button" className="btn btn-primary" onClick={onSubmit} disabled={saving}>
					{i18n.t("whatsappModal.buttons.okAdd")}
				</button>
			) : (
				<button type="button" className="btn btn-primary" onClick={onNext} disabled={saving}>
					{i18n.t("connections.wizard.next")}
				</button>
			)}
		</div>
	);
}

export default function ConnectionWizardModal({ open, onClose, channelId }) {
	const { reload } = useContext(WhatsAppsContext);
	const [wizardStep, setWizardStep] = useState(0);
	const [saving, setSaving] = useState(false);
	const [form, setForm] = useState(INITIAL_FORM);
	const [selectedQueueIds, setSelectedQueueIds] = useState([]);
	const [selectedAutoQueueId, setSelectedAutoQueueId] = useState(null);
	const [queues, setQueues] = useState([]);

	const channelMeta = useMemo(
		() => NATIVE_CHANNELS.find((item) => item.id === channelId),
		[channelId]
	);

	const wizardSteps = useMemo(
		() => (channelId ? getConnectionWizardSteps(channelId) : []),
		[channelId]
	);

	const currentStepId = wizardSteps[wizardStep]?.id;
	const isLastStep = wizardStep >= wizardSteps.length - 1;

	useEffect(() => {
		if (!open) {
			setWizardStep(0);
			setForm(INITIAL_FORM);
			setSelectedQueueIds([]);
			setSelectedAutoQueueId(null);
			return undefined;
		}

		let cancelled = false;

		(async () => {
			try {
				const { data } = await api.get("/queue");
				if (!cancelled) {
					setQueues(Array.isArray(data) ? data : []);
				}
			} catch (err) {
				if (!cancelled) toastError(err);
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [open]);

	const updateField = (name, value) => {
		setForm((current) => ({ ...current, [name]: value }));
	};

	const handleNext = () => {
		if (currentStepId === "basic" && !form.name.trim()) {
			toast.error(i18n.t("connections.wizard.errors.nameRequired"));
			return;
		}
		setWizardStep((current) => Math.min(current + 1, wizardSteps.length - 1));
	};

	const handleSubmit = async () => {
		if (!form.name.trim()) {
			toast.error(i18n.t("connections.wizard.errors.nameRequired"));
			return;
		}

		setSaving(true);
		try {
			const payload = {
				name: form.name.trim(),
				greetingMessage: form.greetingMessage,
				farewellMessage: form.farewellMessage,
				isDefault: Boolean(form.isDefault),
				channel: channelId,
				queueIds: selectedQueueIds,
				maxAttempts: Number(form.maxAttempts) || 3,
			};

			if (selectedAutoQueueId) {
				payload.autoQueueId = selectedAutoQueueId;
			}

			if (channelId === "email") {
				payload.email = form.email;
				payload.emailPassword = form.email ? form.emailPassword : "";
				payload.emailServer = form.emailServer;
				payload.emailPort = form.emailPort;
				payload.emailServerSend = form.emailServerSend;
				payload.emailPortSend = form.emailPortSend;
			} else if (channelId === "whatsappcloud") {
				payload.wabaFoneNumberId = form.wabaFoneNumberId;
				payload.wabaAccountId = form.wabaAccountId;
				payload.wabaAccessToken = form.wabaAccessToken;
			} else if (channelId === "facebook") {
				payload.pageId = form.pageId;
				payload.pageToken = form.pageToken;
			} else if (channelId === "telegram") {
				payload.telegramBotToken = form.telegramBotToken;
			}

			const { data: createdConnection } = await api.post("/whatsapp", payload);
			await reload();
			toast.success(i18n.t("whatsappModal.success"));
			onClose(createdConnection);
		} catch (err) {
			toastError(err);
		} finally {
			setSaving(false);
		}
	};

	const renderStepContent = () => {
		switch (currentStepId) {
			case "basic":
				return (
					<WizardStepPanel
						title={i18n.t("connections.wizard.steps.basic")}
						description={i18n.t("connections.wizard.stepDescriptions.basic")}
					>
						<div className="space-y-4">
							<div>
								<label
									htmlFor="connection-wizard-name"
									className="mb-2 block text-sm font-medium text-default-800 dark:text-default-100"
								>
									{i18n.t("whatsappModal.form.name")}
								</label>
								<input
									id="connection-wizard-name"
									type="text"
									className="form-input w-full"
									value={form.name}
									onChange={(e) => updateField("name", e.target.value)}
									autoFocus
								/>
							</div>
							<label className="flex cursor-pointer items-center gap-3 rounded-lg border border-default-200 px-4 py-3 dark:border-default-700">
								<input
									type="checkbox"
									className="form-checkbox"
									checked={form.isDefault}
									onChange={(e) => updateField("isDefault", e.target.checked)}
								/>
								<span className="text-sm text-default-800 dark:text-default-100">
									{i18n.t("connections.table.default")}
								</span>
							</label>
						</div>
					</WizardStepPanel>
				);

			case "credentials":
				if (channelId === "whatsappcloud") {
					return (
						<WizardStepPanel
							title={i18n.t("connections.wizard.steps.credentials")}
							description={i18n.t("connections.wizard.stepDescriptions.whatsappcloud")}
						>
							<div className="space-y-4">
								<div className="grid gap-4 sm:grid-cols-2">
									<AuthField id="waba-phone" label="WABA ID do número">
										<input
											id="waba-phone"
											type="text"
											className="form-input w-full"
											value={form.wabaFoneNumberId}
											onChange={(e) => updateField("wabaFoneNumberId", e.target.value)}
										/>
									</AuthField>
									<AuthField id="waba-account" label="WABA ID da conta">
										<input
											id="waba-account"
											type="text"
											className="form-input w-full"
											value={form.wabaAccountId}
											onChange={(e) => updateField("wabaAccountId", e.target.value)}
										/>
									</AuthField>
								</div>
								<AuthField id="waba-token" label="Token da API">
									<textarea
										id="waba-token"
										className="form-input min-h-[6rem] w-full resize-y"
										value={form.wabaAccessToken}
										onChange={(e) => updateField("wabaAccessToken", e.target.value)}
										rows={3}
									/>
								</AuthField>
							</div>
						</WizardStepPanel>
					);
				}

				if (channelId === "facebook") {
					return (
						<WizardStepPanel
							title={i18n.t("connections.wizard.steps.credentials")}
							description={i18n.t("connections.wizard.stepDescriptions.facebook")}
						>
							<div className="grid gap-4 sm:grid-cols-2">
								<AuthField id="fb-page-id" label="Page ID">
									<input
										id="fb-page-id"
										type="text"
										className="form-input w-full"
										value={form.pageId}
										onChange={(e) => updateField("pageId", e.target.value)}
									/>
								</AuthField>
								<AuthField id="fb-page-token" label="Page Access Token">
									<input
										id="fb-page-token"
										type="text"
										className="form-input w-full"
										value={form.pageToken}
										onChange={(e) => updateField("pageToken", e.target.value)}
									/>
								</AuthField>
							</div>
						</WizardStepPanel>
					);
				}

				if (channelId === "telegram") {
					return (
						<WizardStepPanel
							title={i18n.t("connections.wizard.steps.credentials")}
							description={i18n.t("connections.wizard.stepDescriptions.telegram")}
						>
							<AuthField id="telegram-token" label="Telegram Bot Token">
								<input
									id="telegram-token"
									type="text"
									className="form-input w-full"
									value={form.telegramBotToken}
									onChange={(e) => updateField("telegramBotToken", e.target.value)}
								/>
							</AuthField>
						</WizardStepPanel>
					);
				}

				if (channelId === "email") {
					return (
						<WizardStepPanel
							title={i18n.t("connections.wizard.steps.credentials")}
							description={i18n.t("connections.wizard.stepDescriptions.email")}
						>
							<div className="space-y-4">
								<div className="grid gap-4 sm:grid-cols-2">
									<AuthField id="email-address" label="E-mail">
										<input
											id="email-address"
											type="email"
											className="form-input w-full"
											value={form.email}
											onChange={(e) => updateField("email", e.target.value)}
										/>
									</AuthField>
									<AuthField id="email-password" label="Senha">
										<input
											id="email-password"
											type="password"
											className="form-input w-full"
											value={form.emailPassword}
											onChange={(e) => updateField("emailPassword", e.target.value)}
										/>
									</AuthField>
								</div>
								<div className="grid gap-4 sm:grid-cols-2">
									<AuthField id="email-server" label="Host de recebimento">
										<input
											id="email-server"
											type="text"
											className="form-input w-full"
											value={form.emailServer}
											onChange={(e) => updateField("emailServer", e.target.value)}
										/>
									</AuthField>
									<AuthField id="email-port" label="Porta de recebimento">
										<input
											id="email-port"
											type="number"
											className="form-input w-full"
											value={form.emailPort}
											onChange={(e) => updateField("emailPort", e.target.value)}
										/>
									</AuthField>
								</div>
								<div className="grid gap-4 sm:grid-cols-2">
									<AuthField id="email-server-send" label="Host de envio">
										<input
											id="email-server-send"
											type="text"
											className="form-input w-full"
											value={form.emailServerSend}
											onChange={(e) => updateField("emailServerSend", e.target.value)}
										/>
									</AuthField>
									<AuthField id="email-port-send" label="Porta de envio">
										<input
											id="email-port-send"
											type="number"
											className="form-input w-full"
											value={form.emailPortSend}
											onChange={(e) => updateField("emailPortSend", e.target.value)}
										/>
									</AuthField>
								</div>
							</div>
						</WizardStepPanel>
					);
				}

				return null;

			case "messages":
				return (
					<WizardStepPanel
						title={i18n.t("connections.wizard.steps.messages")}
						description={i18n.t("connections.wizard.stepDescriptions.messages")}
					>
						<div className="grid gap-4">
							<AuthField id="greeting-message" label={i18n.t("queueModal.form.greetingMessage")}>
								<textarea
									id="greeting-message"
									className="form-input min-h-[6rem] w-full resize-y"
									value={form.greetingMessage}
									onChange={(e) => updateField("greetingMessage", e.target.value)}
									rows={4}
								/>
							</AuthField>
							<AuthField id="farewell-message" label={i18n.t("whatsappModal.form.farewellMessage")}>
								<textarea
									id="farewell-message"
									className="form-input min-h-[6rem] w-full resize-y"
									value={form.farewellMessage}
									onChange={(e) => updateField("farewellMessage", e.target.value)}
									rows={4}
								/>
							</AuthField>
						</div>
					</WizardStepPanel>
				);

			case "queues":
				return (
					<WizardStepPanel
						title={i18n.t("connections.wizard.steps.queues")}
						description={i18n.t("connections.wizard.stepDescriptions.queues")}
					>
						<div className="space-y-4">
							<QueueCheckboxList
								label={i18n.t("queueSelect.inputLabel")}
								options={queues}
								selectedIds={selectedQueueIds}
								onChange={setSelectedQueueIds}
								emptyText="Nenhum setor disponível."
							/>
							<AuthField id="auto-queue" label={i18n.t("autoQueueSelect.inputLabel")}>
								<select
									id="auto-queue"
									className="form-select w-full"
									value={selectedAutoQueueId ?? ""}
									onChange={(e) =>
										setSelectedAutoQueueId(
											e.target.value === "" ? null : Number(e.target.value)
										)
									}
								>
									<option value="">{i18n.t("autoQueueSelect.empty")}</option>
									{queues.map((queue) => (
										<option key={queue.id} value={queue.id}>
											{queue.name}
										</option>
									))}
								</select>
							</AuthField>
							<AuthField id="max-attempts" label={i18n.t("chatbotMaxAttempts")}>
								<input
									id="max-attempts"
									type="number"
									className="form-input w-full"
									value={form.maxAttempts}
									onChange={(e) => updateField("maxAttempts", Number(e.target.value))}
								/>
							</AuthField>
						</div>
					</WizardStepPanel>
				);

			default:
				return null;
		}
	};

	if (!channelId) return null;

	return (
		<AppModal
			open={open}
			onClose={onClose}
			title={i18n.t("whatsappModal.title.add")}
			subtitle={i18n.t("connections.wizard.subtitle")}
			maxWidth={580}
			bodyClassName="!pt-2"
			footer={
				<WizardFooter
					onClose={onClose}
					onBack={() => setWizardStep((current) => Math.max(current - 1, 0))}
					onNext={handleNext}
					onSubmit={handleSubmit}
					showBack={wizardStep > 0}
					isLastStep={isLastStep}
					saving={saving}
				/>
			}
		>
			<ChannelBanner channelMeta={channelMeta} />
			<WizardStepper steps={wizardSteps} currentStep={wizardStep} />
			<div className="min-h-[220px]">{renderStepContent()}</div>
		</AppModal>
	);
}
